<template>
  <div class="brief-content-view">
    <SelectFolderModalToolbar
      :value="searchKey"
      @input="searchKey = $event"
      @clear="searchKey = ''"
    >
      <template #breadcrumb>
        <slot
          name="breadcrumb"
          :title="briefTitle"
        />
      </template>
    </SelectFolderModalToolbar>

    <div
      v-if="isLoading"
      class="brief-content-view__state"
    >
      <b-spinner />
    </div>

    <div
      v-else-if="visibleItems.length === 0"
      class="brief-content-view__state"
    >
      {{ emptyStateText }}
    </div>

    <ItemGrid
      v-else
      class="brief-content-view__grid"
      :items="visibleItems"
      :is-selected="isSelected"
      :item-props="{ showCheckbox: true, showDownloadIcon: true }"
      @item-click="toggleSelect"
      @download="downloadItem"
    />
  </div>
</template>

<script>
import ItemGrid from '@frontend/components/modules/file-browser/browser/ItemGrid.vue';
import SelectFolderModalToolbar from '@frontend/components/modules/file-browser/modals/SelectFolderModalToolbar.vue';
import { downloadFileFromUrl } from '@frontend/utils';
import { getBrief } from '@frontend/components/briefs/briefsApi';
import { collectBriefPickerImages } from '@frontend/components/briefs/briefReferenceUtils';
import { FILE_TYPE } from '@frontend/constants/file-image-types';

export default {
  name: 'BriefContentView',

  components: {
    ItemGrid,
    SelectFolderModalToolbar,
  },

  props: {
    briefId: {
      type: [String, Number],
      required: true,
    },
  },

  data() {
    return {
      items: [],
      searchKey: '',
      selectedItems: [],
      isLoading: false,
      loadFailed: false,
      briefTitle: '',
    };
  },

  computed: {
    emptyStateText() {
      if (this.loadFailed) {
        return 'Could not load the brief images.';
      }

      return this.searchKey.trim() ? 'No images match your search.' : 'No images found in this brief.';
    },
    visibleItems() {
      const query = this.searchKey.trim().toLowerCase();

      if (!query) {
        return this.items;
      }

      return this.items.filter((item) => (item.name || '').toLowerCase().includes(query));
    },
  },

  watch: {
    briefId() {
      this.fetchReferences();
    },
  },

  created() {
    this.fetchReferences();
  },

  methods: {
    async fetchReferences() {
      const requestedBriefId = this.briefId;

      this.items = [];
      this.selectedItems = [];
      this.searchKey = '';
      this.loadFailed = false;
      this.briefTitle = '';
      this.isLoading = true;
      this.$emit('onSelectedItems', []);

      try {
        const res = await getBrief(requestedBriefId, { internal: true });

        // A slower earlier request must not overwrite the brief the user is on now.
        if (requestedBriefId !== this.briefId) {
          return;
        }

        const brief = res && res.brief ? res.brief : null;

        this.briefTitle = (brief && brief.title) || '';

        // Inspiration rows plus images embedded in every section (Creative Direction
        // and the rest). The same file can sit in more than one section.
        this.items = collectBriefPickerImages(brief).map((image) => this.toBrowserItem(image));
      } catch (error) {
        if (requestedBriefId !== this.briefId) {
          return;
        }

        console.error('Error fetching brief references:', error);
        this.loadFailed = true;
        toastr.error('Failed to load brief images');
      } finally {
        if (requestedBriefId === this.briefId) {
          this.isLoading = false;
        }
      }
    },

    // drawFileSvg needs the normalized 'image' token, not the extension, or it renders nothing.
    toBrowserItem(image) {
      return {
        id: image.id,
        name: image.name,
        type: FILE_TYPE.IMAGE,
        path: image.url,
        url: image.url,
        asset_type: 'file_asset',
      };
    },

    isSelected(item) {
      return this.selectedItems.some((i) => i.id === item.id);
    },

    toggleSelect(item) {
      this.selectedItems = this.isSelected(item)
        ? this.selectedItems.filter((i) => i.id !== item.id)
        : [...this.selectedItems, item];

      this.$emit('onSelectedItems', this.selectedItems);
    },

    async downloadItem(item) {
      await downloadFileFromUrl(item.url, item.name);
    },
  },
};
</script>

<style scoped>
.brief-content-view {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 360px;
}

.brief-content-view__state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  color: #73808c;
}

.brief-content-view__grid {
  max-height: 60vh;
  overflow-y: auto;
}
</style>
